import { setAssistantOpen } from './assistant-store'

/**
 * The ONE Yes/No question the assistant is currently waiting on, and the
 * promise the browser's client-tool executor is parked on until the user
 * answers it.
 *
 * This is the whole product's confirmation surface, not this task's: Tasks 7
 * and 9 ask their bulk and schema questions through this same store, this same
 * card and this same executor. There is exactly one of each, and a second one
 * would be a second consent channel nobody audits.
 *
 * React-free module store, in the shape of `assistant-store.ts` (and
 * `robot-alerts-store.ts` / `sos-call-store.ts` before it) — a module-level
 * `Set<Listener>` plus a module-level snapshot read by `useSyncExternalStore`,
 * so `getConfirmSnapshot()` MUST return the same reference until something
 * genuinely changes.
 *
 * Module-level and not React context for the usual reason plus a sharper one:
 * the thing that needs to ASK (`AssistantProvider`'s client-tool effect) and the
 * thing that needs to RENDER (`AssistantSurface`, mounted in `AppLayout`) are
 * different subtrees, and the asker is a plain async function inside a promise
 * chain, not a component.
 *
 * ONE at a time. `executeClientToolBatch` runs its calls strictly in order and
 * awaits each, and only one run is ever in flight, so a second request while one
 * is pending is not a queue that needs draining — it is a stale question the
 * server has already replaced (one `pendingEdit` per session). It is answered
 * NO rather than dropped: an unanswered client tool suspends the server's run
 * forever, and declining is the safe half of that choice.
 */
export interface AssistantConfirmationRequest {
  confirmationId: string
  action: string
  subject: string
  title: string
  details: Record<string, string>
}

/** Why a question came back NO, for the model to relay. Absent on a Yes. */
export type ConfirmDecline = 'declined' | 'timed_out' | 'superseded' | 'dismissed'

export interface ConfirmAnswer {
  confirmed: boolean
  reason?: ConfirmDecline
}

export interface AssistantConfirmationState {
  pending: AssistantConfirmationRequest | null
}

/**
 * A user who walks away must not leave the server's run suspended: the run's own
 * `RUN_TTL_MS` is 15 minutes (`services/console/ai/src/services/runBudget.ts`),
 * so four minutes answers long before that, and long before a Task 7 token's own
 * five-minute TTL. A timeout answers NO, like every other non-click.
 */
export const CONFIRM_DEADLINE_MS = 4 * 60_000

type Listener = () => void

const listeners = new Set<Listener>()

let state: AssistantConfirmationState = { pending: null }
let resolver: ((answer: ConfirmAnswer) => void) | null = null
let timer: ReturnType<typeof setTimeout> | null = null

function notify(): void {
  listeners.forEach(l => l())
}

export function subscribeConfirm(listener: Listener): () => void {
  listeners.add(listener)

  return () => {
    listeners.delete(listener)
  }
}

export function getConfirmSnapshot(): AssistantConfirmationState {
  return state
}

function settle(answer: ConfirmAnswer): void {
  const resolve = resolver

  if (timer) clearTimeout(timer)
  timer = null
  resolver = null
  state = { pending: null }
  // Notify BEFORE resolving: the card comes off screen as the answer travels,
  // rather than lingering while the continuation turn is already in flight.
  notify()
  resolve?.(answer)
}

/**
 * Ask, and resolve when the user clicks.
 *
 * NEVER rejects. A rejection would leave the server's run suspended forever
 * waiting for a result that can never arrive — the rule `executeClientTool`
 * states for every client tool, and the one this tool can actually break,
 * because it is the only one that does not resolve immediately.
 *
 * The panel is opened FIRST, before `pending` is set, so no interleaving of the
 * two notifications can ever render a pending question into a closed panel —
 * the state a viewer could not answer.
 */
export function requestConfirmation(request: AssistantConfirmationRequest): Promise<ConfirmAnswer> {
  // A second question supersedes the first rather than stacking two cards: the
  // server holds one pending confirmation per session, so the older card is
  // already asking about something that no longer exists.
  if (resolver) settle({ confirmed: false, reason: 'superseded' })

  setAssistantOpen(true)
  state = { pending: request }
  notify()

  return new Promise<ConfirmAnswer>(resolve => {
    resolver = resolve
    timer = setTimeout(() => settle({ confirmed: false, reason: 'timed_out' }), CONFIRM_DEADLINE_MS)
  })
}

/**
 * The click. Ignores an answer aimed at anything but the question on screen —
 * the id is checked rather than assumed, because a stale card resolving the
 * CURRENT question is an approval the user gave to something else.
 */
export function answerConfirmation(confirmationId: string, confirmed: boolean): void {
  if (!state.pending || state.pending.confirmationId !== confirmationId) return
  settle(confirmed ? { confirmed: true } : { confirmed: false, reason: 'declined' })
}

/**
 * Resolve a pending question as NO without a click — used when the user closes,
 * clears or stops the conversation. An unanswered question the user can no
 * longer see is a suspended run; declining is the only outcome that is both
 * visible to the model and safe (nothing is written).
 */
export function dismissConfirmation(reason: ConfirmDecline = 'dismissed'): void {
  if (!resolver) return
  settle({ confirmed: false, reason })
}

/** Test-only reset. Resolves anything parked so a pending promise never leaks between tests. */
export function __resetConfirmForTests(): void {
  if (resolver) settle({ confirmed: false, reason: 'dismissed' })
  listeners.clear()
  if (timer) clearTimeout(timer)
  timer = null
  resolver = null
  state = { pending: null }
}
